import { CompanyTypeEnum, Country, useApiTradeV1TradeCompaniesCreate } from 'api';
import {
  CompanyTypeSelectField,
  CountryAutocompleteField,
  Form,
  type InferType,
  TextField,
  useFormWithApiErrors,
  yup,
  yupResolver,
} from 'form';
import { type I18nScopedKey, type I18nTranslateFunction, useTranslation } from 'i18n';
import { Button } from 'ui';

type FeaturesTranslate = I18nTranslateFunction<I18nScopedKey<'features'>>;

const getSchema = (t: FeaturesTranslate) =>
  yup.object().shape({
    company_type: yup
      .string()
      .oneOf(Object.values(CompanyTypeEnum))
      .required(t('auth.companySignUpForm.validation.companyTypeRequired')),
    country: yup
      .string()
      .oneOf(Object.values(Country))
      .required(t('auth.companySignUpForm.validation.countryRequired')),
    name: yup.string().required(t('auth.companySignUpForm.validation.nameRequired')),
  });

type FormValues = InferType<ReturnType<typeof getSchema>>;
export const CompanySignUpForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation('features');
  const schema = getSchema(t);
  const form = useFormWithApiErrors({
    resolver: yupResolver(schema),
  });

  const { mutate, isPending } = useApiTradeV1TradeCompaniesCreate({
    mutation: {
      onError(error) {
        form.setApiErrors(error, { errorMessage: t('auth.companySignUpForm.notifications.errorSigningUp') });
      },
      onSuccess() {
        onSuccess?.();
      },
    },
  });

  const onSubmit = (data: FormValues) => {
    mutate({
      data,
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit} data-testid="company-sign-up-form">
      <TextField
        type="text"
        name="name"
        size="medium"
        label={t('auth.companySignUpForm.labels.name')}
        required
        autoFocus
        fullWidth
      />
      <CountryAutocompleteField
        name="country"
        size="medium"
        label={t('auth.companySignUpForm.labels.country')}
        required
        fullWidth
      />
      <CompanyTypeSelectField
        name="company_type"
        size="medium"
        label={t('auth.companySignUpForm.labels.companyType')}
        required
      />
      <Button type="submit" size="large" loading={isPending}>
        {t('auth.companySignUpForm.actions.next')}
      </Button>
    </Form>
  );
};
